import { HttpErrorResponse } from '@angular/common/http';
import { ValvurApiErrorBody } from './valvur-api-error.model';
import {
  VALVUR_ERROR_CODE,
  VALVUR_GENERIC_ERROR_MESSAGE,
} from './valvur-error-codes';
import { ValvurHttpError, isValvurHttpError } from './valvur-http.error';

export function isValvurApiErrorBody(value: unknown): value is ValvurApiErrorBody {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const body = value as ValvurApiErrorBody;
  return (
    typeof body.statusCode === 'number' &&
    typeof body.errorCode === 'string' &&
    typeof body.message === 'string'
  );
}

/**
 * Maps `HttpErrorResponse` to the gateway contract `{ statusCode, errorCode, message }`.
 * Falls back for legacy Nest bodies without `errorCode`.
 */
export function parseValvurApiError(
  response: HttpErrorResponse,
): ValvurApiErrorBody {
  const statusCode = response.status || 0;
  const body = response.error;

  if (isValvurApiErrorBody(body)) {
    return {
      statusCode: body.statusCode || statusCode,
      errorCode: body.errorCode,
      message: body.message,
      ...(body.meta ? { meta: body.meta } : {}),
    };
  }

  let message = response.message || 'Http failure';
  let errorCode: string = VALVUR_ERROR_CODE.UNSPECIFIED;

  if (typeof body === 'string') {
    message = body;
  } else if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    if (typeof record['errorCode'] === 'string') {
      errorCode = record['errorCode'];
    }
    if (typeof record['message'] === 'string') {
      message = record['message'];
    } else if (Array.isArray(record['message'])) {
      message = String(record['message'][0] ?? message);
    }
  }

  if (errorCode === VALVUR_ERROR_CODE.COMMON_VALIDATION_FAILED) {
    // Keep field-specific validator copy from the gateway.
  } else if (statusCode >= 500 && errorCode === VALVUR_ERROR_CODE.UNSPECIFIED) {
    errorCode = VALVUR_ERROR_CODE.UNKNOWN;
    message = VALVUR_GENERIC_ERROR_MESSAGE;
  }

  const meta =
    body && typeof body === 'object'
      ? readValvurApiErrorMeta(body as Record<string, unknown>)
      : undefined;

  return { statusCode, errorCode, message, ...(meta ? { meta } : {}) };
}

function readValvurApiErrorMeta(
  record: Record<string, unknown>,
): ValvurApiErrorBody['meta'] | undefined {
  const meta = record['meta'];
  if (!meta || typeof meta !== 'object') {
    return undefined;
  }
  const m = meta as Record<string, unknown>;
  const out: NonNullable<ValvurApiErrorBody['meta']> = {};

  const suggestions = m['userNameSuggestions'];
  if (
    Array.isArray(suggestions) &&
    suggestions.every((s) => typeof s === 'string')
  ) {
    out.userNameSuggestions = suggestions;
  }
  const scopeKey = m['scopeKey'];
  if (typeof scopeKey === 'string' && scopeKey.trim()) {
    out.scopeKey = scopeKey.trim();
  }
  const planCode = m['planCode'];
  if (typeof planCode === 'string') {
    out.planCode = planCode;
  }
  for (const key of ['limit', 'used', 'remaining'] as const) {
    const value = m[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = value;
    }
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

export function toValvurHttpError(response: HttpErrorResponse): ValvurHttpError {
  return new ValvurHttpError(parseValvurApiError(response), response);
}

/** Read normalized API error from any thrown value (interceptor or raw HttpErrorResponse). */
export function getValvurApiError(error: unknown): ValvurApiErrorBody | null {
  if (isValvurHttpError(error)) {
    return error.apiError;
  }
  if (error instanceof HttpErrorResponse) {
    return parseValvurApiError(error);
  }
  return null;
}
